import { Autocomplete, Button, Checkbox, FormControlLabel, Grid, Link, List, ListItem, TextField } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { setLoader } from 'reducers/appConfigReducer';
import { selectBrUid } from 'selectors/accountSelector';
import { AppDispatch, RootState } from 'store';
import { closeServiceAgreement, moveForwardServiceAgreement } from 'thunks/accountServiceAgreementsThunk';
import { jbColors } from 'theme/jbColors';
import { setContactNoteId, setInBulkClosing, setSelectedServiceAgreement } from 'reducers/serviceAgreementsReducer';
import { selectPartnerId } from 'selectors/partnerStatusSelector';

const INTERMEDIARIES_LEGAL_OPINION_MEMOS_URL =
  'https://wiki.juliusbaer.com/pages/viewpage.action?pageId=455547537&spaceKey=cocintermediaries&title=Legal%2BOpinions%2BIntermediaries';

interface ContactNote {
  contactNoteId: string;
  topics?: string[];
  subject?: string;
}

interface AttestationProps {
  checkedProp?: boolean;
  contactNoteIdProp?: string;
  taskId?: string | null;
  closeAction: () => void;
  isReadOnly: boolean;
  contactList?: (string | ContactNote)[];
}

const Attestation = ({
  checkedProp,
  contactNoteIdProp,
  taskId,
  closeAction,
  isReadOnly,
  contactList,
}: AttestationProps) => {
  const [checked, setChecked] = React.useState<boolean>(checkedProp ?? false);
  const dispatch = useDispatch<AppDispatch>();
  const selectedServiceAgreement = useSelector((state: RootState) => state.serviceAgreements.selectedServiceAgreement);
  const inBulkClosing = useSelector((state: RootState) => state.serviceAgreements.inBulkClosing);
  const agreementsToClose = useSelector((state: RootState) => state.serviceAgreements.agreementsToClose);
  const brUid = useSelector(selectBrUid);
  const partnerId = useSelector(selectPartnerId);
  const navigate = useNavigate();
  const [ccnId, setCcnId] = React.useState<string>(contactNoteIdProp ?? '');
  const [selectText, setSelectText] = React.useState<string>('');
  const contactNoteId = useSelector((state: RootState) => state.serviceAgreements.contactNoteId);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };
  const features = useSelector((state: RootState) => state.userFeatureToggles.features);
  const isButtonEnabled = () =>
    (brUid && features.BUSINESS_RELATION.BulkClosure) || (partnerId && features.PARTNER.BulkClosure);

  const handleSetContactNoteId = (newValue: string | null) => {
    if (newValue) {
      const item: ContactNote =
        typeof newValue === 'object' && 'contactNoteId' in newValue ? (newValue as ContactNote) : JSON.parse(newValue);
      const id = item.contactNoteId;

      setSelectText(newValue);
      setCcnId(id);
      dispatch(setContactNoteId(id));
    } else {
      setSelectText('');
      setCcnId('');
      dispatch(setContactNoteId(''));
    }
  };

  const getTextForContactNote = (contactNote: string | ContactNote | null | undefined) => {
    if (!contactNote || contactNote === '') return;

    const item: ContactNote =
      typeof contactNote === 'object' && 'contactNoteId' in contactNote ? contactNote : JSON.parse(contactNote);
    const contactNoteId = item?.contactNoteId;
    const topics = item?.topics;
    const subject = item?.subject;

    return `${contactNoteId} (${topics?.map((t) => t).join(', ')} - ${subject})`;
  };

  const handleSubmit = async () => {
    if (agreementsToClose && inBulkClosing && agreementsToClose?.length > 0) {
      dispatch(setInBulkClosing(false));
      dispatch(setSelectedServiceAgreement(null));
      dispatch(closeServiceAgreement(agreementsToClose, true, checked));
      dispatch(setContactNoteId(''));
    } else {
      if (!selectedServiceAgreement?.serviceAgreementId) return;
      const formData = JSON.stringify({
        checked: checked,
        contactNoteId: ccnId,
      });

      dispatch(
        setLoader({
          loading: true,
          message: 'Submitting...',
        }),
      );

      await dispatch(
        moveForwardServiceAgreement(
          taskId ?? undefined,
          selectedServiceAgreement?.serviceAgreementId,
          formData,
          undefined,
        ),
      );
      closeAction();
      navigate(`/accounts/${brUid}/serviceAgreement/${selectedServiceAgreement?.processInstanceId}/configuration`);
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
    }
  };

  return (
    <Grid container data-testid="attestation">
      <Grid item xs={12} sx={{ mt: 1, mb: 0.5 }}>
        <FormControlLabel
          label={
            'I confirm to have documented in a Client Contact Note in CLM in accordance with applicable policies (e.g. Country Guidelines; Intermediaries Legal Opinion Memos):'
          }
          control={
            <Checkbox
              data-testid="attestation-checkbox"
              required={!checked}
              defaultChecked={checked}
              value={checked}
              onChange={handleChange}
              disabled={isReadOnly}
            />
          }
        />
      </Grid>

      <Grid item xs={12} sx={{ mt: 1, mb: 0.5 }}>
        <List
          sx={{
            listStyleType: 'disc',
            listStylePosition: 'inside',
            '& .MuiListItem-root': {
              color: 'black',
              fontSize: '12px',
              fontWeight: 'normal',
            },
          }}
        >
          <ListItem sx={{ display: 'list-item' }}>
            who first contacted whom, where incl. background description of contact
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>who initiated onboarding/opening/services</ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            what products and services were discussed/agreed, at whose request, and where?
          </ListItem>
        </List>
      </Grid>

      <Grid item xs={12} sx={{ mt: 1, mb: 0.5 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => {
            window.open('https://apps3.juliusbaer.com/sites/spnmarth22/Pages/Countries-Overview.aspx', '_blank');
          }}
        >
          Country Guidelines - Markets Overview
        </Link>
      </Grid>

      <Grid item xs={12} sx={{ mt: 1, mb: 0.5 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => {
            window.open(INTERMEDIARIES_LEGAL_OPINION_MEMOS_URL, '_blank');
          }}
        >
          Intermediaries Legal Opinion Memos - All Documents
        </Link>
      </Grid>

      {contactList && contactList.length > 0 && (
        <Grid item xs={12}>
          <Autocomplete
            data-testid="contact-list-dropdown"
            options={contactList as string[]}
            getOptionLabel={(option) => getTextForContactNote(option) ?? ''}
            value={selectText || null}
            onChange={(_event, newValue) => handleSetContactNoteId(newValue)}
            renderOption={(props, option, { index }) => {
              let keyVal = option;
              if (option.trim().startsWith('{')) {
                try {
                  const item = JSON.parse(option);
                  keyVal = item.contactNoteId ?? option;
                } catch (e) {
                  console.debug('Failed to parse contact note JSON:', e);
                }
              }
              return (
                <li {...props} data-testid={`contact-list-option-${index}`} className="menu-item" key={keyVal}>
                  {getTextForContactNote(option)}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                required={!ccnId}
                label="Contact list"
                variant="outlined"
                size="small"
                sx={{ width: '40em', borderRadius: 0, marginTop: 1 }}
              />
            )}
          />
        </Grid>
      )}

      {(contactList === undefined || contactList?.length === 0) && (
        <Grid item xs={12} sx={{ mt: 4, mb: 0.5 }}>
          <TextField
            required={!ccnId}
            value={ccnId ?? contactNoteId}
            onChange={(e) => {
              dispatch(setContactNoteId(e.target.value));
              setCcnId(e.target.value);
            }}
            label="CCN-ID"
            disabled={isReadOnly}
          ></TextField>
        </Grid>
      )}

      <Grid container sx={{ mr: 1.2, borderRadius: 0 }}></Grid>
      <Grid item xs={5} />
      <Grid container item xs={7} sx={{ display: 'flex' }}>
        <Grid
          item
          xs={6}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            whiteSpace: 'nowrap',
          }}
        />
        <Grid
          item
          xs={6}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            whiteSpace: 'nowrap',
          }}
        >
          {!isReadOnly && (
            <Button
              id="attestation-button"
              data-testid="attestation-submit-button"
              sx={{
                backgroundColor: jbColors.core.reflexBlue[100],
                color: 'white',
                '&:hover': {
                  backgroundColor: 'white',
                  color: jbColors.core.reflexBlue[100],
                },
                '&:disabled': {
                  border: `1px solid ${jbColors.support.solidGrayIcon[10]}`,
                  backgroundColor: jbColors.core.stone[10],
                  color: jbColors.support.solidGrayIcon[10],
                },
              }}
              onClick={handleSubmit}
              disabled={!checked || ccnId === '' || !isButtonEnabled}
            >
              Submit
            </Button>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Attestation;
